import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import moment from "moment";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Links from "../../Data/Links";
import "./SiteVisitDealerReport.css";

const allowedDivisionKeys = new Set(["auto", "used", "rec", "drivenation", "ffun cars"]);

const getStoreOptionLabel = (store) => {
  const division = String(store?.division || "").trim();
  const name = String(store?.name || "").trim();

  if (division === "DriveNation") {
    return `Drive Nation ${name}`.trim();
  }

  if (division === "FFUN Cars") {
    return `FFUN Cars ${name}`.trim();
  }

  return name;
};

const SECTION_CONFIG = [
  {
    key: "exterior",
    label: "Exterior",
    questions: [
      { key: "exterior-sidewalk", label: "Winter - Sidewalk cleared and salted" },
      { key: "exterior-windows", label: "Windows clean" },
      { key: "exterior-vehicles", label: "Vehicles aligned, evenly spaced and clean" },
      { key: "exterior-plate-brackets", label: "FFUN plate brackets on all units" },
      { key: "exterior-stickers", label: "FFUN stickers on all units" },
      { key: "exterior-promotional", label: "Promotional material visible and current" },
      { key: "exterior-flags", label: "FFUN flags up and in good shape" }
    ],
    noteKey: "exterior-notes"
  },
  {
    key: "interior",
    label: "Interior - First Impression",
    questions: [
      { key: "interior-welcome", label: "Warm welcome upon entering" },
      { key: "interior-no-clutter", label: "No clutter in entrance" },
      { key: "interior-music", label: "Music on and appropriate volume" },
      { key: "interior-floors", label: "Floors clean" }
    ],
    noteKey: "interior-notes"
  },
  {
    key: "showroom",
    label: "Showroom",
    questions: [
      { key: "showroom-appearance", label: "Appropriate clothing and appearance" },
      { key: "showroom-campaign", label: "Sales campaign visible and current" },
      { key: "showroom-brochures", label: "Brochures stocked" },
      { key: "showroom-desks", label: "Desks clean" }
    ],
    noteKey: "showroom-notes"
  },
  {
    key: "customer",
    label: "Customer Areas",
    questions: [
      { key: "customer-coffee-water", label: "Coffee/water stocked" },
      { key: "customer-seating", label: "Seating clean and arranged" },
      { key: "customer-magazines", label: "Magazines current" },
      { key: "customer-charging", label: "Charging stations functional" },
      { key: "customer-tv", label: "TV content brand-aligned" },
      { key: "customer-bathroom", label: "Bathroom clean and well supplied" }
    ],
    noteKey: "customer-notes"
  },
  {
    key: "staff",
    label: "Staff Areas",
    questions: [
      { key: "staff-lunch-room", label: "Lunch room clean and user friendly" },
      { key: "staff-core-values", label: "Core values signage visible" },
      { key: "staff-ohc-minutes", label: "OHC minutes posted and within last three months" }
    ],
    noteKey: "staff-notes"
  },
  {
    key: "parts",
    label: "Parts & Service",
    questions: [
      { key: "parts-appearance", label: "Appropriate clothing and appearance" },
      { key: "parts-campaign", label: "Service campaign visible and current" },
      { key: "parts-drive-through", label: "Drive through clean and organized" },
      { key: "parts-shop", label: "Shop clean and organized" },
      { key: "parts-counter", label: "Parts counter clean and organized" },
      { key: "parts-tire-display", label: "Tire display visible and current" },
      { key: "parts-merchandise", label: "Merchandise faced and dust free" }
    ],
    noteKey: "parts-notes"
  }
];

const normalizeAnswer = (value) => String(value || "").trim().toLowerCase();

const formatAnswer = (value) => {
  const normalized = normalizeAnswer(value);
  if (normalized === "yes") return "Yes";
  if (normalized === "no") return "No";
  if (normalized === "na") return "N/A";
  return "-";
};

const toPercent = (value) => {
  if (value === null || Number.isNaN(value)) return "-";
  return `${Math.round(value)}%`;
};

const summarizeAnswers = (answers = {}) => {
  const counts = { yes: 0, no: 0, na: 0 };
  Object.values(answers).forEach((value) => {
    const normalized = normalizeAnswer(value);
    if (normalized === "yes" || normalized === "no" || normalized === "na") {
      counts[normalized] += 1;
    }
  });
  const scoredTotal = counts.yes + counts.no;
  const score = scoredTotal ? (counts.yes / scoredTotal) * 100 : null;
  return { counts, score };
};

const SiteVisitDealerReport = () => {
  const [stores, setStores] = useState(() => {
    const cached = localStorage.getItem("stores");
    return cached ? JSON.parse(cached) : [];
  });
  const [selectedStoreId, setSelectedStoreId] = useState("");
  const [submissions, setSubmissions] = useState([]);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const [expandedId, setExpandedId] = useState(null);

  const filteredStores = useMemo(() => {
    return Array.isArray(stores)
      ? stores.filter((store) =>
          allowedDivisionKeys.has(String(store?.division || "").toLowerCase())
        )
      : [];
  }, [stores]);

  useEffect(() => {
    if (!filteredStores.length) return;
    if (!selectedStoreId) {
      setSelectedStoreId(filteredStores[0].id);
    }
  }, [filteredStores, selectedStoreId]);

  useEffect(() => {
    const fetchSubmissions = async () => {
      if (!selectedStoreId) return;
      try {
        setStatus("loading");
        const response = await axios.get(Links.getSiteVisitChecklists, {
          params: {
            storeId: selectedStoreId,
            company: "FFUN",
            limit: 200,
            offset: 0
          }
        });
        const payload = response?.data || {};
        const items = payload.data || [];
        setSubmissions(Array.isArray(items) ? items : []);
        setStatus("ready");
      } catch (err) {
        console.error("Site visit dealer report fetch error:", err);
        setError("Unable to load submissions.");
        setStatus("error");
      }
    };

    fetchSubmissions();
  }, [selectedStoreId]);

  const selectedStore = filteredStores.find(
    (store) => String(store.id) === String(selectedStoreId)
  );

  const summary = useMemo(() => {
    const totals = submissions.reduce(
      (acc, entry) => {
        const { counts } = summarizeAnswers(entry.answers);
        acc.yes += counts.yes;
        acc.no += counts.no;
        acc.na += counts.na;
        return acc;
      },
      { yes: 0, no: 0, na: 0 }
    );
    const scoredTotal = totals.yes + totals.no;
    const score = scoredTotal ? (totals.yes / scoredTotal) * 100 : null;
    return { totals, score };
  }, [submissions]);

  return (
    <div className="svd-page">
      <div className="svd-hero">
        <div>
          <p className="svd-eyebrow">Dealer Report</p>
          <h1>Site Visit Dealer Report</h1>
          <p className="svd-subtitle">
            Detailed submissions for a single store with answer distribution.
          </p>
        </div>
        <div className="svd-picker">
          <FormControl variant="standard" sx={{ minWidth: 220 }}>
            <InputLabel id="svd-store-label">Select store</InputLabel>
            <Select
              labelId="svd-store-label"
              value={selectedStoreId}
              onChange={(event) => setSelectedStoreId(event.target.value)}
            >
              {filteredStores.map((store) => (
                <MenuItem key={store.id} value={store.id}>
                  {getStoreOptionLabel(store)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
      </div>

      <div className="svd-summary">
        <div className="svd-card">
          <span className="svd-card-label">Store</span>
          <h3>{selectedStore ? getStoreOptionLabel(selectedStore) : "-"}</h3>
        </div>
        <div className="svd-card">
          <span className="svd-card-label">Submissions</span>
          <h3>{submissions.length}</h3>
        </div>
        <div className="svd-card">
          <span className="svd-card-label">Avg Score</span>
          <h3>{toPercent(summary.score)}</h3>
        </div>
        <div className="svd-card">
          <span className="svd-card-label">Yes / No / N/A</span>
          <h3>
            {summary.totals.yes} / {summary.totals.no} / {summary.totals.na}
          </h3>
        </div>
      </div>

      <div className="svd-table-wrap">
        {status === "loading" && <div className="svd-empty">Loading submissions...</div>}
        {status === "error" && <div className="svd-empty">{error}</div>}
        {status === "ready" && submissions.length === 0 && (
          <div className="svd-empty">No submissions found for this store.</div>
        )}

        {status === "ready" && submissions.length > 0 && (
          <table className="svd-table">
            <thead>
              <tr>
                <th>Submitted</th>
                <th>Submitted By</th>
                <th>Score</th>
                <th>Yes</th>
                <th>No</th>
                <th>N/A</th>
                <th>Notes</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {submissions.map((entry, index) => {
                const { counts, score } = summarizeAnswers(entry.answers);
                const notes = entry.notes || {};
                const noteCount = Object.values(notes).filter((value) => String(value || "").trim()).length;
                const rowId = entry.id ?? `${entry.submittedAt || "row"}-${index}`;
                const isExpanded = expandedId === rowId;
                const answers = entry.answers || {};
                return (
                  <React.Fragment key={rowId}>
                    <tr>
                      <td>{entry.submittedAt ? moment(entry.submittedAt).format("MMM D, YYYY") : "-"}</td>
                      <td>{entry.submittedBy || "-"}</td>
                      <td className="svd-score">{toPercent(score)}</td>
                      <td>{counts.yes}</td>
                      <td>{counts.no}</td>
                      <td>{counts.na}</td>
                      <td>{noteCount}</td>
                      <td>
                        <button
                          type="button"
                          className="svd-detail-toggle"
                          onClick={() => setExpandedId(isExpanded ? null : rowId)}
                        >
                          {isExpanded ? "Hide" : "View"}
                        </button>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="svd-detail-row">
                        <td colSpan={8}>
                          <div className="svd-detail">
                            {SECTION_CONFIG.map((section) => {
                              const noteValue = String(notes?.[section.noteKey] || "").trim();
                              return (
                                <div key={section.key} className="svd-detail-section">
                                  <h4>{section.label}</h4>
                                  <div className="svd-detail-grid">
                                    {section.questions.map((question) => (
                                      <div key={question.key} className="svd-detail-item">
                                        <span className="svd-detail-key">{question.label}</span>
                                        <span
                                          className={`svd-detail-value svd-answer-chip svd-answer-${normalizeAnswer(
                                            answers?.[question.key]
                                          )}`}
                                        >
                                          {formatAnswer(answers?.[question.key])}
                                        </span>
                                      </div>
                                    ))}
                                    <div className="svd-detail-item svd-note-item">
                                      <span className="svd-detail-key">Notes</span>
                                      <span className="svd-detail-value">{noteValue || "-"}</span>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default SiteVisitDealerReport;
